import { Header, ErrorBoundary } from "@/components/layout";
import { SkipLink } from "@/components/layout";
import Footer from "@/components/Footer";
import { NavigationLoader } from "@/components/loading";
import { useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";
import { getStoredConsent } from "@/utils/consent";
import { ConsentBanner } from "@/components/shared";
import { useHeroAnimation } from "@/hooks/useHeroAnimation";
import { initAnalytics } from "@/utils/analytics";
import { initMarketingPixels } from "@/utils/marketingPixels";

import type { ReactNode } from "react";

interface LayoutProps {
  children: ReactNode;
}

export default function Layout({ children }: LayoutProps) {
  const { showHeader } = useHeroAnimation();
  const _location = useLocation();
  const footerRef = useRef<HTMLDivElement>(null);

  // Show footer immediately after layout is calculated
  useEffect(() => {
    // Use multiple requestAnimationFrame calls to ensure layout is fully calculated
    const showFooter = () => {
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          if (footerRef.current) {
            footerRef.current.style.display = "";
            footerRef.current.style.visibility = "";
          }
          document.body.classList.add("react-ready");
        });
      });
    };

    // Show footer immediately - no delay
    showFooter();
  }, []);

  // Initialize analytics if consent already given - deferred until after page is interactive
  useEffect(() => {
    // Wait for page to be interactive before loading analytics
    const initAnalyticsWhenReady = () => {
      // Check if page is interactive
      if (document.readyState === "complete") {
        loadAnalytics();
      } else {
        window.addEventListener("load", loadAnalytics, { once: true });
      }
    };

    const loadAnalytics = () => {
      // Further defer until browser is idle
      if ("requestIdleCallback" in window) {
        window.requestIdleCallback(
          () => {
            checkAndInitAnalytics();
            checkAndInitMarketingPixels();
          },
          { timeout: 3000 }
        );
      } else {
        // Fallback for browsers without requestIdleCallback
        setTimeout(() => {
          checkAndInitAnalytics();
          checkAndInitMarketingPixels();
        }, 2000);
      }
    };

    const checkAndInitAnalytics = () => {
      const consent = getStoredConsent();
      if (consent?.accepted && consent.analytics) {
        // Add preconnect for faster connection
        const preconnect = document.createElement("link");
        preconnect.rel = "preconnect";
        preconnect.href = "https://www.googletagmanager.com";
        document.head.appendChild(preconnect);

        const preconnect2 = document.createElement("link");
        preconnect2.rel = "preconnect";
        preconnect2.href = "https://www.google-analytics.com";
        document.head.appendChild(preconnect2);

        initAnalytics();
      }
    };

    const checkAndInitMarketingPixels = () => {
      const consent = getStoredConsent();
      if (consent?.accepted && consent.marketing) {
        // Add preconnect for faster connection to pixel domains
        const preconnectMeta = document.createElement("link");
        preconnectMeta.rel = "preconnect";
        preconnectMeta.href = "https://connect.facebook.net";
        document.head.appendChild(preconnectMeta);

        const preconnectTwitter = document.createElement("link");
        preconnectTwitter.rel = "preconnect";
        preconnectTwitter.href = "https://static.ads-twitter.com";
        document.head.appendChild(preconnectTwitter);

        const preconnectTikTok = document.createElement("link");
        preconnectTikTok.rel = "preconnect";
        preconnectTikTok.href = "https://analytics.tiktok.com";
        document.head.appendChild(preconnectTikTok);

        initMarketingPixels();
      }
    };

    initAnalyticsWhenReady();
  }, []);

  return (
    <>
      {/* Navigation loading indicator */}
      <NavigationLoader />

      {/* Skip to main content link for keyboard navigation */}
      <SkipLink />

      {/* Header */}
      <Header isVisible={showHeader} />

      {/* Master page wrapper - constrains all content to 1600px max width */}
      <div className="w-full flex justify-center min-h-screen">
        <div className="w-full max-w-[1600px] flex flex-col min-h-screen">
          {/* Main content */}
          <main
            id="main-content"
            role="main"
            aria-label="Main content"
            className="relative w-full flex flex-col flex-1 pt-[72px] min-[481px]:pt-[96px] min-[2200px]:pt-[108px]"
            tabIndex={-1}
          >
            {children}
          </main>

          {/* Footer */}
          <div
            ref={footerRef}
            className="relative z-10 pb-12 md:pb-16 lg:pb-20 mt-auto"
            style={{
              display: "none",
              visibility: "hidden",
            }}
          >
            <Footer />
          </div>
        </div>
      </div>

      {/* Consent Banner - Direct import to avoid ad blocker blocking */}
      <ErrorBoundary fallback={null}>
        <ConsentBanner />
      </ErrorBoundary>
    </>
  );
}
